import { useState } from 'react';
import { Alert, App, Button, Card, Form, Modal, Space, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { AdminStation, CreateStationBody } from '@goelectric/shared-types';
import { stationApi } from '../api/stationApi';
import { StationForm } from '../components/StationForm';
import { describeStationApiError } from '../utils/stationErrors';
import { stationDisplayName } from '../utils/format';
import type { StationFormState } from '../utils/stationForm';

/**
 * Página de alta de Cargador (`/cargadores/nuevo`, solo `SUPER_ADMIN` — el guard de la ruta
 * lo garantiza). Compone el wizard `StationForm` con el POST y el resultado: en éxito muestra
 * SIEMPRE el modal de confirmación; recién al cerrarlo navega al detalle del cargador creado.
 * En error muestra el `message` del backend y marca el campo si el error lo identifica (ADR 0021).
 *
 * Espejo de `CreateLocationPage`: el alta puede completarse en el CRM pero fallar la
 * sincronización con el core CitrineOS (`syncStatus` `PENDIENTE`/`ERROR`); el modal advierte
 * ese caso y ofrece reintentar desde el detalle.
 */
export function CreateStationPage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [form] = Form.useForm<StationFormState>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Campo que marcó el backend en el último error; el wizard salta a su paso si no es el actual.
  const [errorField, setErrorField] = useState<string | null>(null);
  // Confirmación del alta: siempre presente tras crear; trae los datos y el `syncStatus`.
  const [confirmation, setConfirmation] = useState<AdminStation | null>(null);

  const goToList = () => navigate('/cargadores');

  /** Cierra el modal (o el X/ESC) y recién ahí navega al detalle del cargador creado. */
  const closeConfirmation = () => {
    const stationId = confirmation?.id;
    setConfirmation(null);
    if (stationId) {
      navigate(`/cargadores/${encodeURIComponent(stationId)}`);
    }
  };

  const handleSubmit = async (body: CreateStationBody) => {
    setSubmitting(true);
    setError(null);
    setErrorField(null);
    try {
      const created = await stationApi.create(body);
      message.success('Cargador creado.');
      setConfirmation(created);
    } catch (caught) {
      const { message: detail, field } = describeStationApiError(caught);
      setError(detail);
      setErrorField(field ?? null);
      if (field) {
        form.setFields([{ name: field as keyof StationFormState, errors: [detail] }]);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // El core pudo no confirmar la ChargingStation: el alta sirvió pero falta sincronizar.
  const syncPending = confirmation !== null && confirmation.syncStatus !== 'SYNCED';

  return (
    <div className="cargadores-page">
      <div className="cargadores-page__header">
        <Space align="center">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            aria-label="Volver al listado de cargadores"
            onClick={goToList}
          />
          <Typography.Title level={3} style={{ margin: 0 }}>
            Nuevo cargador
          </Typography.Title>
        </Space>
      </div>

      {error && (
        <Alert
          type="error"
          showIcon
          role="alert"
          message="No se pudo crear el cargador"
          description={error}
        />
      )}

      <Card>
        <StationForm
          form={form}
          onSubmit={handleSubmit}
          submitting={submitting}
          errorField={errorField}
        />
      </Card>

      <Modal
        open={confirmation !== null}
        rootClassName="cargadores-created-modal"
        title={
          <Typography.Title
            level={3}
            className="cargadores-created-modal__headline"
            style={{ margin: 0 }}
          >
            Tenemos un nuevo cargador
          </Typography.Title>
        }
        onCancel={closeConfirmation}
        footer={
          <Button type="primary" onClick={closeConfirmation}>
            Ver detalles del cargador
          </Button>
        }
        width={640}
      >
        <Typography.Paragraph className="cargadores-created-modal__lead">
          Se creó el cargador{' '}
          <Typography.Text strong>{confirmation ? stationDisplayName(confirmation) : ''}</Typography.Text>.
        </Typography.Paragraph>

        {syncPending ? (
          <Typography.Paragraph
            className="cargadores-created-modal__lead"
            style={{ marginBottom: 0 }}
          >
            El cargador quedó creado, pero{' '}
            <Typography.Text strong>todavía no está sincronizado con el core</Typography.Text>.
            Puedes reintentar la sincronización desde el detalle.
          </Typography.Paragraph>
        ) : (
          <Typography.Paragraph
            className="cargadores-created-modal__lead"
            style={{ marginBottom: 0 }}
          >
            El cargador ya está sincronizado con el core.
          </Typography.Paragraph>
        )}
      </Modal>
    </div>
  );
}
